/**
 * @coeldery/share-button — **分享**（手機用系統分享選單；唔支援就退 WhatsApp 連結）
 *
 * 為何係模組（R3；`pending_changes` **P-74**）：任何 app 都有「分享呢件嘢」嘅需要；
 *   而「Web Share API → 退 wa.me」係一段固定做法，重複寫會各處唔一致。
 *
 * 血緣：cosingery（2026-09-22 產品負責人：「i want … can share through whatsapp after confirm the song」／
 *   同日第二批：「the share button, it only share the link of CoSingery, **but not the song itself**,
 *   so can we actually sharing a song?」）—— 原本寫喺 `ChatPage` 內，依 R3 抽成模組。
 *
 * ⚠️ 設計：
 *   · 模組**唔識**產品內容 —— `title`／`text`／`url`／`fileUrl` 全部由父層傳
 *   · **三級路徑（由好到差；一律照實，唔准扮成功）**：
 *       ① `navigator.share({ files })` → 收件人收到**真檔案**（例 mp3，WhatsApp 直接播）
 *       ② `navigator.share({ title, text })` → 交畀系統選單
 *       ③ `https://wa.me/?text=…` → 只係**文字＋連結**（會誠實顯示 `share_file_fallback_note`）
 *   · 用戶**主動取消**（`AbortError`）→ 靜靜收工（唔准再彈第二個介面）
 *
 * ⭐ **1.3.0**（2026-09-23；產品負責人「sharing still not working」）：**失敗要講得出原因**——
 *   `noteWithError()`／`errorName()` 會把系統回報嘅錯誤名（`NotAllowedError`／`TypeError`／`DataError`…）
 *   附喺誠實筆記後面；另外「有檔案但唔支援分享檔案」亦有專門提示（`share_file_unsupported_note`）。
 *   為何：之前只講「分享失敗」→ 只能盲猜（手勢？唔支援？檔案太大？）→ 一輪又一輪試。
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shareFilename } from './files.ts';

export { shareFilename } from './files.ts';

export interface ShareButtonProps {
  title: string;
  text?: string;
  url?: string;
  /** ⭐ 1.1.0：要分享嘅**檔案**網址（例：mp3；同源 → 前端自己 fetch）。唔支援／失敗 → 老實退文字 */
  fileUrl?: string;
  /** ⭐ 1.1.0：檔名（唔傳 → 由 `title` 砌，見 `shareFilename()`） */
  filename?: string;
  /**
   * ⭐ **1.2.0**：**已經準備好嘅檔案**（父層提早 fetch；撳掣即刻分享 —— 保留用戶手勢）。
   *   有佢就會跳過 download，直接 `navigator.share({files})`（**第一件事就係 share**）。
   */
  prefetchedFile?: File | null;
  /** ⭐ 1.2.0：攞檔案嘅逾時（毫秒；預設 60000）—— 逾時就**照實講** ＋ 退文字 */
  timeoutMs?: number;
  /** 掣面文字（i18n key；預設 `share_button`） */
  labelKey?: string;
  /** 額外樣式（父層可以跟隔籬掣一致） */
  style?: React.CSSProperties;
  /** 已經由父層處理分享（唔傳 ＝ 用內建邏輯） */
  onShare?: () => void;
}

/** 砌分享內容（文字＋連結）—— 純函數（可單測） */
export function buildShareText(title: string, text = '', url = ''): string {
  return [title, text, url].filter((s) => s && s.trim().length > 0).join('\n\n');
}

/** WhatsApp 後備連結（純函數；可單測） */
export function whatsappHref(payload: string): string {
  return `https://wa.me/?text=${encodeURIComponent(payload)}`;
}

/** 用戶主動取消分享（唔可以當錯誤） */
function isAbort(err: unknown): boolean {
  return (err as { name?: string } | null)?.name === 'AbortError';
}

/**
 * ⭐ **1.3.0**：攞出系統回報嘅**錯誤名**（例 `NotAllowedError`／`TypeError`／`DataError`）
 *   · 為何要：產品負責人實測「分享仍然唔 work」→ 但「唔 work」有好幾個唔同原因，
 *     **唔講出邊個**就只可以盲猜。照實顯示名字 → 一次就知係手勢／唔支援／檔案問題。
 *   · 純函數（可單測）；冇名字就回空字串。
 */
export function errorName(err: unknown): string {
  const name = (err as { name?: string } | null)?.name;
  return typeof name === 'string' && name.trim().length > 0 ? name.trim() : '';
}

/** ⭐ 1.3.0：砌「誠實筆記」（有錯誤名就附上）—— 純函數（可單測） */
export function noteWithError(base: string, err: unknown): string {
  const name = errorName(err);
  return name ? `${base}（${name}）` : base;
}

/** ⭐ 1.2.0：可以分享檔案？ */
export function canShareFiles(nav: Navigator & { canShare?: (data: ShareData) => boolean }, file: File): boolean {
  return !nav.canShare || nav.canShare({ files: [file] });
}

/** ⭐ 1.3.0：瀏覽器嘅 `navigator`（有 `canShare` 就帶埋）*/
function navWithCanShare(): Navigator & { canShare?: (data: ShareData) => boolean } {
  return navigator as Navigator & { canShare?: (data: ShareData) => boolean };
}

/** ⭐ 1.2.0：帶逾時嘅 fetch（唔准無限等；逾時回 `null` 由呼叫者照實講） */
async function fetchWithTimeout(url: string, timeoutMs: number): Promise<Response | null> {
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = setTimeout(() => ctrl?.abort(), timeoutMs);
  try {
    return await fetch(url, ctrl ? { signal: ctrl.signal } : undefined);
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

export default function ShareButton({
  title,
  text = '',
  url = '',
  fileUrl,
  filename,
  prefetchedFile = null,
  timeoutMs = 60000,
  labelKey = 'share_button',
  style,
  onShare,
}: ShareButtonProps) {
  const { t } = useTranslation();
  /** 狀態提示（**誠實**：準備緊／逾時／退咗文字） */
  const [note, setNote] = useState<string | null>(null);
  /** ⭐ 1.2.0：攞檔案期間要**顯示處理緊**（唔准靜靜轉圈） */
  const [busy, setBusy] = useState(false);

  /**
   * ⭐ 1.2.0：分享一個檔案（或者退文字）—— **有 file 就第一件事做 share**（保住用戶手勢）
   *   回 `true` ＝ 已經交咗畀系統（或者用戶取消）；`false` ＝ 要再試其他路徑
   */
  const shareFileOrText = async (file: File | null): Promise<boolean> => {
    const payload = buildShareText(title, text, url);
    const nav = navWithCanShare();
    if (file && canShareFiles(nav, file)) {
      try {
        await navigator.share({ files: [file], title, text: payload });
        setNote(null);
        return true;
      } catch (err) {
        if (isAbort(err)) return true;
        /** 系統拒絕（多數係用戶手勢已過期）→ **照實講**（附系統錯誤名 → 唔使盲猜），然後退文字 */
        setNote(noteWithError(t('share_gesture_note'), err));
      }
    }
    try {
      await navigator.share({ title, text: payload });
      return true;
    } catch (err) {
      if (isAbort(err)) return true;
      /** ⭐ 1.3.0：連文字都失敗 → **照實講原因**（唔准只係靜靜當「分享咗」） */
      setNote(noteWithError(t('share_failed_note'), err));
      return false;
    }
  };

  const share = async () => {
    if (onShare) {
      onShare();
      return;
    }
    const canWebShare = typeof navigator !== 'undefined' && 'share' in navigator;
    const payload = buildShareText(title, text, url);

    /** ⭐ 1.3.0：**有檔案但呢部機唔支援分享檔案** → 照實講（唔好靜靜當冇事） */
    if (canWebShare && prefetchedFile && !canShareFiles(navWithCanShare(), prefetchedFile)) {
      setNote(t('share_file_unsupported_note'));
    }

    /** ① ⭐ **有預先準備好嘅檔案 → 即刻分享**（唔可以 await 其他嘢先，否則會失去用戶手勢） */
    if (canWebShare && prefetchedFile && (await shareFileOrText(prefetchedFile))) return;

    /** ② 未準備好 → 攞檔案（顯示「準備緊」＋**逾時**）→ 再分享 */
    if (canWebShare && fileUrl) {
      setBusy(true);
      setNote(t('share_preparing'));
      const res = await fetchWithTimeout(fileUrl, timeoutMs);
      setBusy(false);
      if (res && res.ok) {
        try {
          const blob = await res.blob();
          const file = new File([blob], filename || shareFilename(title), { type: blob.type || 'audio/mpeg' });
          if (await shareFileOrText(file)) return;
        } catch {
          /* 落下面照實講（唔准靜靜失敗） */
        }
      } else {
        /** 攞唔到（逾時／404／網慢）→ **照實講** */
        setNote(t('share_timeout_note'));
      }
      setNote((prev) => prev ?? t('share_file_fallback_note'));
    }

    /** ③ 最後後備：WhatsApp 連結（只係文字＋連結） */
    if (typeof window !== 'undefined') window.open(whatsappHref(payload), '_blank');
  };

  return (
    <span
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: '2px',
        fontFamily: 'inherit',
      }}
    >
      <button
        type="button"
        data-testid="share-button"
        disabled={busy}
        aria-busy={busy}
        onClick={() => void share()}
        style={{ fontFamily: 'inherit', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1, ...style }}
      >
        {busy ? t('share_preparing') : t(labelKey)}
      </button>
      {note ? (
        <span
          role="status"
          data-testid="share-note"
          style={{ fontSize: 'var(--font-size-tiny, 12px)', color: 'var(--color-text-secondary)', maxWidth: '22em' }}
        >
          {note}
        </span>
      ) : null}
    </span>
  );
}

